import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Send, ChevronLeft, MoreVertical, Video, Phone, CheckCheck, Bot, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PageHeader from "@/components/PageHeader";
import WaText from "@/components/WaText";
import { errMsg, getBusiness, getSimulator, simReset, simSend } from "@/lib/api";
import { dayjs } from "@/lib/format";

const SUGGESTIONS = [
  "Hola, ¿qué venden?",
  "¿Cuál es su horario?",
  "Quiero hacer un pedido",
  "Quiero reservar una mesa para 4",
  "Quiero hablar con un asesor",
];

function PhoneBubble({ m }) {
  const mine = m.sender_role === "customer";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} animate-msg`}>
      <div className={`max-w-[80%] px-2.5 py-1.5 rounded-lg shadow-sm text-[13.5px] leading-snug whitespace-pre-wrap ${
        mine ? "bg-[#D9FDD3] bubble-out" : "bg-white bubble-in"}`}>
        {m.sender_role === "agent" && <p className="text-[11px] font-bold text-sky-600">{m.sender_name || "Asesor"}</p>}
        <WaText text={m.text} />
        <span className="float-right ml-2 mt-1 text-[10px] text-slate-500 flex items-center gap-0.5">
          {dayjs(m.timestamp).format("HH:mm")}
          {mine && <CheckCheck className="h-3 w-3 text-sky-500" />}
        </span>
      </div>
    </div>
  );
}

function Typing() {
  return (
    <div className="flex justify-start" data-testid="sim-typing">
      <div className="bg-white rounded-lg bubble-in px-3 py-2.5 shadow-sm flex gap-1">
        {[0, 1, 2].map((i) => <span key={i} className="typing-dot h-1.5 w-1.5 rounded-full bg-slate-400" />)}
      </div>
    </div>
  );
}

export default function Simulator() {
  const [phone, setPhone] = useState("5215550001");
  const [name, setName] = useState("Cliente de prueba");
  const [biz, setBiz] = useState({});
  const [state, setState] = useState({ conversation: null, messages: [] });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const epoch = useRef(0);

  useEffect(() => { getBusiness().then(setBiz).catch(() => {}); }, []);

  useEffect(() => {
    epoch.current += 1;
    setState({ conversation: null, messages: [] });
    setError("");
    const load = () => {
      if (document.hidden) return;
      const e = epoch.current;
      getSimulator(phone).then((s) => { if (e === epoch.current) setState(s); }).catch(() => {});
    };
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [phone]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [state.messages.length, busy]);

  const send = async (value) => {
    const t = (value ?? text).trim();
    if (!t || busy) return;
    epoch.current += 1;
    const requestEpoch = epoch.current;
    setError("");
    setText("");
    setBusy(true);
    setState((s) => ({ ...s, messages: [...s.messages, { id: `tmp-${Date.now()}`, sender_role: "customer", text: t, timestamp: new Date().toISOString() }] }));
    try {
      const result = await simSend({ phone, name, text: t });
      if (requestEpoch === epoch.current) setState(result);
    }
    catch (e) {
      const message = errMsg(e, "No se pudo enviar");
      if (requestEpoch === epoch.current) setError(message);
      toast.error(message);
    }
    finally { setBusy(false); }
  };

  const reset = async () => {
    epoch.current += 1;
    await simReset(phone);
    epoch.current += 1;
    setState({ conversation: null, messages: [] });
    toast.success("Conversación reiniciada");
  };

  const conv = state.conversation;
  const title = biz.name || "Mi negocio";

  return (
    <div className="h-full overflow-y-auto scroll-thin" data-testid="simulator-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <PageHeader title="Prueba tu asistente" subtitle="Chatea como un cliente y comprueba las respuestas de la IA con los datos de tu negocio antes de conectar WhatsApp." />
        {error && <p role="alert" className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</p>}
        <div className="grid lg:grid-cols-[380px_1fr] gap-10 items-start">
          <div className="mx-auto w-full max-w-[360px] h-[720px] rounded-[3rem] border-[12px] border-[#111B21] bg-[#111B21] shadow-2xl overflow-hidden flex flex-col relative" data-testid="sim-phone">
            <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-24 rounded-full bg-black z-10" />
            <div className="bg-[#008069] text-white pt-9 pb-2.5 px-3 flex items-center gap-2">
              <ChevronLeft className="h-5 w-5" />
              <div className="h-9 w-9 rounded-full bg-emerald-200 text-emerald-800 flex items-center justify-center font-bold text-sm">{title[0]}</div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[15px] truncate" data-testid="sim-business-name">{title}</p>
                <p className="text-[11px] text-emerald-100">{busy ? "escribiendo…" : "en línea"}</p>
              </div>
              <Video className="h-5 w-5" /><Phone className="h-4 w-4 ml-2" /><MoreVertical className="h-5 w-5 ml-1" />
            </div>
            <div ref={scrollRef} className="flex-1 wa-wallpaper overflow-y-auto scroll-thin px-3 py-3 space-y-1.5" data-testid="sim-messages">
              <div className="mx-auto w-fit text-[11px] bg-[#FFF5C4] text-slate-600 rounded-md px-2.5 py-1 shadow-sm text-center max-w-[90%]">
                Los mensajes son de prueba y no se envían a WhatsApp real.
              </div>
              {state.messages.map((m) => <PhoneBubble key={m.id} m={m} />)}
              {busy && <Typing />}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="bg-[#F0F2F5] px-2 py-2 flex gap-2 items-center">
              <input data-testid="sim-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Mensaje"
                className="flex-1 h-10 rounded-full bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
              <button data-testid="sim-send-button" type="submit" disabled={!text.trim() || busy}
                className="h-10 w-10 rounded-full bg-[#00A884] text-white flex items-center justify-center disabled:opacity-50 hover:bg-[#008f6f] transition-colors">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>

          <div className="space-y-6">
            <section className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-display text-lg font-bold">Cliente simulado</h3>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div><Label>Nombre</Label><Input data-testid="sim-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" /></div>
                <div><Label>Teléfono (cualquiera)</Label><Input data-testid="sim-phone-input" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ""))} className="mt-1.5" /></div>
              </div>
              <Button variant="outline" data-testid="sim-reset-button" onClick={reset} className="mt-4 rounded-full">
                <RotateCcw className="h-4 w-4 mr-2" />Reiniciar conversación
              </Button>
            </section>
            <section className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-display text-lg font-bold">Prueba estas frases</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={s} data-testid={`sim-suggestion-${i}`} onClick={() => send(s)} disabled={busy}
                    className="px-3.5 py-2 rounded-full border border-slate-200 text-sm hover:border-emerald-400 hover:bg-emerald-50 transition-colors disabled:opacity-50">
                    {s}
                  </button>
                ))}
              </div>
            </section>
            {conv && (
              <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="sim-status">
                <h3 className="font-display text-lg font-bold">Estado en la bandeja</h3>
                <div className="mt-3 flex flex-wrap gap-2 text-sm">
                  {conv.needs_human ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold"><UserRound className="h-3.5 w-3.5" />Necesita un humano</span>
                  ) : conv.bot_enabled ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold"><Bot className="h-3.5 w-3.5" />El bot está respondiendo</span>
                  ) : (
                    <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">Atendido por un asesor</span>
                  )}
                  {(conv.tags || []).map((t) => <span key={t} className="px-3 py-1 rounded-full bg-sky-50 text-sky-700 font-semibold">#{t}</span>)}
                </div>
                <p className="mt-3 text-sm text-slate-500">Esta conversación también aparece en <b>Chats</b>; si un asesor responde allí, lo verás en el teléfono.</p>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
